import React, { useEffect, useState } from 'react';
import {
	Alert,
	AlertColor,
	Backdrop,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ICoin, IFilledOrderFormVN, OrderCreatedResultVN, OrderFormFieldsMaxLengthVN } from '../../types';
import { checkShowErrorMessage } from 'features/common/helpers';
import { getCoinsByOrderTypeVN, getFileByDealId, postFormDataVN } from 'features/payment/services';
import { findContactsDataByDealID } from 'services';
import { AutocompleteContactData } from 'features/quotation/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import { SendClientLinkModal } from '../SendClientLinkModal';
import { emailPattern } from 'features/common/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import BitrixDealOrCaseSwitch from '../../../common/components/BitrixDealOrCaseSwitch';
import BitrixDealOrCaseUserInfoConfirm from '../../../common/components/BitrixDealOrCaseUserInfoConfirm';
import { getNimbuxCaseSummary } from '../../../common/services';

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	helperTextDanger: {
		color: 'red',
	},
}));

export const OrderFormViajeNaranja = (): JSX.Element => {
	const classes = useStyles();
	const { auth } = useSelector((state: RootState) => state);

	const orderFormDefaultValues = {
		crmTicket: 0,
		name: '',
		email: '',
		phone: '',
		file: '',
		fee: [1, 2, 3, 4, 5, 6],
		coinId: 10,
		amount: 0,
		description: '',
		plCountryCode: auth.country,
	};

	const {
		control,
		handleSubmit,
		watch,
		trigger,
		formState: { errors, isValid },
		getValues,
		setValue,
		reset,
	} = useForm({ mode: 'all', defaultValues: orderFormDefaultValues });
	const [crmTicketAfterConfirm, setCrmTicketAfterConfirm] = useState<number>(0);
	const [selectedContactDataOptionIndex, setSelectedContactDataOptionEmail] = useState<number | null>(null);
	const [filesByDealId, setFilesByDealId] = useState<string[]>([]);
	const [currencyToOrder, setCurrencyToOrder] = useState<ICoin[]>([]);
	const [contactDataOptions, setContactDataOptions] = useState<AutocompleteContactData[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = React.useState<ShowAlertState>(defaultAlertState);
	const [orderCreatedResult, setOrderCreatedResult] = useState<OrderCreatedResultVN | null>(null);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [isBitrixDeal, setIsBitrixDeal] = useState<boolean>(false);
	const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
	const crmTicketInFormValue = watch('crmTicket');
	const theme = useTheme();
	const [isPrePopulated, setIsPrePopulated] = useState<boolean>(false);

	const onSubmit = async () => {
		try {
			setLoading(true);
			const { amount, crmTicket } = getValues();
			const clientLink = await postFormDataVN({
				...getValues(),
				isBitrixDeal: isBitrixDeal,
				amount: +amount,
				crmTicket: +crmTicket,
			} as IFilledOrderFormVN);
			setOrderCreatedResult(clientLink.data);
			setAlert({
				show: true,
				severity: 'success',
				message: 'Orden de pago creada satisfactoriamente',
			});
			reset();
			setCrmTicketAfterConfirm(0);
			setContactDataOptions([]);
			setSelectedContactDataOptionEmail(null);
			setLoading(false);
			setModalOpen(true);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la orden de pago'),
			});
			setLoading(false);
		}
	};

	const handleCloseModal = () => {
		setOrderCreatedResult(null);
		setModalOpen(false);
	};

	const handleDealId = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			setLoading(true);
			setCrmTicketAfterConfirm(getValues('crmTicket'));
			setLoading(false);
		}
	};

	const handleSelectContactDataOption = (index: number) => setSelectedContactDataOptionEmail(index);

	const getDataAfterDealId = async () => {
		let files: string[] = [];
		let newContactData = [{ name: '', phone: '', email: '' }] as AutocompleteContactData[];
		if (crmTicketAfterConfirm) {
			try {
				setIsConfirmed(false);
				setLoading(true);
				files = (await getFileByDealId(crmTicketAfterConfirm)).data;
				newContactData = (await findContactsDataByDealID(crmTicketAfterConfirm)).data;
				setContactDataOptions([...newContactData]);
				setLoading(false);
				setIsConfirmed(true);
			} catch (error: any) {
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Ocurrió un error en la peticion de datos correspondientes al deal ID'),
				});
				setLoading(false);
			}
		}
		setFilesByDealId(files);
	};

	const getDataOfCase = async () => {
		if (crmTicketAfterConfirm) {
			try {
				setIsConfirmed(false);
				setLoading(true);
				const response = await getNimbuxCaseSummary(crmTicketAfterConfirm);
				if (!response.data?.client) {
					setAlert({
						show: true,
						severity: 'error',
						message: 'El caso no tiene cliente asociado, por favor asocie un cliente al caso',
					});
					setContactDataOptions([]);
				} else {
					const client = response.data.client;
					if (!client.email) {
						setAlert({
							show: true,
							severity: 'error',
							message: 'Por favor complete el email del cliente en el caso para el envío de la orden de pago',
						});
						setContactDataOptions([]);
					} else {
						const autocompleteContactData = {
							name: `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim(),
							email: client.email,
							phone: client.completePhoneNumber,
						} as AutocompleteContactData;
						setContactDataOptions([autocompleteContactData]);
						setIsConfirmed(true);
					}
				}

				setLoading(false);
			} catch (error: any) {
				const errorMessage = extractErrorMessage(error, 'Ocurrió un error al buscar el caso');
				setAlert({
					show: true,
					severity: 'error',
					message: errorMessage,
				});
				setLoading(false);
			} finally {
				setFilesByDealId([]);
			}
		}
	};

	const getCoinsAtLoad = async () => {
		try {
			setLoading(true);
			const coinIds = (await getCoinsByOrderTypeVN()).data;
			setCurrencyToOrder(coinIds);
			setLoading(false);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al pedir las Monedas'),
			});
			setLoading(false);
		}
	};

	useEffect(() => {
		getCoinsAtLoad();
	}, []);

	useEffect(() => {
		if (isConfirmed) {
			setIsConfirmed(false);
			setSelectedContactDataOptionEmail(null);
			setContactDataOptions([]);
			setCrmTicketAfterConfirm(0);
			setValue('name', '');
			setValue('phone', '');
			setValue('email', '');
			trigger(['name', 'phone', 'email']);
		}
	}, [crmTicketInFormValue]);

	useEffect(() => {
		if (isBitrixDeal) {
			getDataAfterDealId();
		} else {
			getDataOfCase();
		}
	}, [crmTicketAfterConfirm]);

	useEffect(() => {
		if (selectedContactDataOptionIndex !== null) {
			setValue('name', contactDataOptions[selectedContactDataOptionIndex].name);
			setValue('phone', contactDataOptions[selectedContactDataOptionIndex].phone || '');
			setValue('email', contactDataOptions[selectedContactDataOptionIndex].email || '');
			trigger(['name', 'phone', 'email']);
		}
	}, [selectedContactDataOptionIndex]);

	useEffect(() => {
		setSelectedContactDataOptionEmail(null);
		setContactDataOptions([]);
		reset();
		setCrmTicketAfterConfirm(0);
	}, [isBitrixDeal]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const dealId = params.get('dealId');
		const caseId = params.get('caseId');

		if (dealId || caseId) {
			setIsPrePopulated(true);
			const idToUse = dealId || caseId;
			if (idToUse) {
				setValue('crmTicket', parseInt(idToUse));
				setCrmTicketAfterConfirm(parseInt(idToUse));
				setIsBitrixDeal(!!dealId);
			}
		}
	}, []);

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Grid container gap={2}>
					<Grid item xs={12} display="flex" justifyContent="center">
						<Typography variant="h6">Nueva Orden de Pago Viaje Naranja</Typography>
					</Grid>
					<Grid item xs={12}>
						<BitrixDealOrCaseSwitch
							isBitrixDeal={isBitrixDeal}
							onChange={() => !isPrePopulated && setIsBitrixDeal(!isBitrixDeal)}
							disabled={isPrePopulated}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="crmTicket"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								pattern: {
									value: /^(0|[1-9]\d*)(\.\d+)?$/,
									message: 'El valor debe ser numérico y mayor que 0',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label={isBitrixDeal ? 'Deal ID' : 'Caso'}
									type="number"
									disabled={loading || isPrePopulated}
									onKeyPress={handleDealId}
									helperText={checkShowErrorMessage(Boolean(errors.crmTicket), errors.crmTicket?.message)}
									error={Boolean(errors.crmTicket)}
									{...field}
								/>
							)}
						/>
						<BitrixDealOrCaseUserInfoConfirm isBitrixDeal={isBitrixDeal} />
					</Grid>
					{contactDataOptions.length > 0 && (
						<Grid item xs={12}>
							<Paper sx={{ width: '100%' }}>
								<Typography sx={{ padding: 2 }}>Seleccione un contacto:</Typography>
								<List
									component="nav"
									aria-label="Device settings"
									sx={{ bgcolor: 'background.paper', maxHeight: '250px', overflowY: 'scroll' }}
								>
									{contactDataOptions.map((option, index) => (
										<MenuItem
											key={'contact-option-' + index}
											selected={index === selectedContactDataOptionIndex}
											onClick={() => handleSelectContactDataOption(index)}
										>
											<ListItem id={'lock-button-' + index} aria-haspopup="listbox" aria-controls="lock-menu">
												<ListItemText primary={option.name} secondary={`${option.phone},${option.email}`} />
											</ListItem>
										</MenuItem>
									))}
								</List>
							</Paper>
						</Grid>
					)}
					<Grid item xs={12}>
						<Controller
							name="file"
							control={control}
							render={({ field }) => (
								<FormControl sx={{ width: '100%', height: '100%' }}>
									<InputLabel id="file-label">File</InputLabel>
									<Select
										labelId="file-label"
										id="file-select"
										label="File"
										disabled={filesByDealId.length < 1 || loading}
										sx={{ width: '100%' }}
										{...field}
									>
										{filesByDealId.map((file) => {
											return (
												<MenuItem key={file} value={file}>
													{file}
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="name"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								maxLength: {
									value: OrderFormFieldsMaxLengthVN.NAME,
									message:
										'El Nombre excede el largo máximo permitido de: ' + OrderFormFieldsMaxLengthVN.NAME + ' caracteres',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Nombre"
									disabled={loading}
									helperText={checkShowErrorMessage(Boolean(errors.name), errors.name?.message)}
									error={Boolean(errors.name)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="phone"
							control={control}
							rules={{
								maxLength: {
									value: OrderFormFieldsMaxLengthVN.PHONE,
									message:
										'El telefono excede el largo máximo permitido de: ' +
										OrderFormFieldsMaxLengthVN.PHONE +
										' caracteres',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									fullWidth
									label="Teléfono"
									disabled={loading}
									helperText={checkShowErrorMessage(Boolean(errors.phone), errors.phone?.message)}
									error={Boolean(errors.phone)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="email"
							control={control}
							rules={{
								maxLength: {
									value: OrderFormFieldsMaxLengthVN.EMAIL,
									message:
										'El correo electrónico excede el largo máximo permitido de: ' +
										OrderFormFieldsMaxLengthVN.EMAIL +
										' caracteres',
								},
								pattern: {
									value: emailPattern,
									message: 'Ingrese un email válido',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									fullWidth
									label="Email"
									disabled={loading}
									helperText={checkShowErrorMessage(Boolean(errors.email), errors.email?.message)}
									error={Boolean(errors.email)}
									{...field}
								/>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<Controller
							name="coinId"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Por favor seleccione la moneda a usar',
								},
							}}
							render={({ field }) => (
								<FormControl sx={{ width: '100%', height: '100%' }}>
									<InputLabel id="coinId-label">Moneda</InputLabel>
									<Select
										labelId="coinId-label"
										id="coinId-select"
										label="Moneda"
										disabled={!currencyToOrder.length || loading}
										sx={{ width: '100%' }}
										{...field}
									>
										{currencyToOrder.map((currency) => {
											return (
												<MenuItem key={currency.id} value={currency.id}>
													{currency.code}
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="amount"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								min: {
									value: 1,
									message: 'El monto debe ser mayor que 0',
								},
								pattern: {
									value: /^(0|[1-9]\d*)(\.\d+)?$/,
									message: 'El valor debe ser numérico',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Monto"
									type="number"
									disabled={loading}
									helperText={checkShowErrorMessage(Boolean(errors.amount), errors.amount?.message)}
									error={Boolean(errors.amount)}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="description"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Este campo es requerido',
								},
							}}
							render={({ field }) => (
								<>
									<TextField
										fullWidth
										label="Descripción"
										variant="outlined"
										disabled={loading}
										helperText={checkShowErrorMessage(Boolean(errors.description), errors.description?.message)}
										error={Boolean(errors.description)}
										multiline
										rows={4}
										{...field}
									/>
									<Typography variant="body2" sx={{ color: theme.palette.success.main }}>
										Escriba una breve descripción sobre lo que va a pagar el cliente con esta orden. Esta descripción se
										le mostrará al cliente en el sitio web.
									</Typography>
								</>
							)}
						/>
					</Grid>
					<Grid item xs={12} display={'flex'} gap={'10px'} justifyContent="flex-end">
						<Button variant="contained" disabled={loading || !isValid} color="success" fullWidth onClick={onSubmit}>
							Generar Orden de Pago
						</Button>
					</Grid>
				</Grid>
			</form>
			{orderCreatedResult && (
				<SendClientLinkModal
					id={orderCreatedResult.id}
					handleClose={handleCloseModal}
					open={modalOpen}
					link={orderCreatedResult.link}
					isViajeNaranja={true}
				/>
			)}
			<Snackbar open={alert.show} autoHideDuration={2500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
